import { useCallback, useEffect, useState } from 'react';

/**
 * Access and set values on the browser's local storage by using a React hook.
 *
 * @param key - the key to use for the local storage.
 * @returns - A tuple for the current value and a set value function.
 */
export function useLocalStorage<T>(
  key: string,
  initialValue: T | null = null
): readonly [T | null, (value: T | null) => void] {
  const [storedValue, setStoredValue] = useState<T | null>(() => {
    // window is sometimes undefined (e.g. in SSR context)
    if (typeof window === 'undefined') {
      return initialValue;
    }

    try {
      const item = window.localStorage.getItem(key);

      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.log(error);

      return initialValue;
    }
  });

  useEffect(() => {
    // subscribe to storage
    const handleStorageChange = (event: StorageEvent) => {
      console.log(event);
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  });

  const setValue = useCallback(
    (value: T | null) => {
      try {
        setStoredValue(value);

        // window is sometimes undefined (e.g. in SSR context)
        if (typeof window !== 'undefined') {
          if (value === null) {
            window.localStorage.removeItem(key);
          } else {
            window.localStorage.setItem(key, JSON.stringify(value));
          }
        }
      } catch (error) {
        console.log(error);
      }
    },
    [key]
  );

  return [storedValue, setValue] as [T | null, (value: T | null) => void];
}
